import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { SynthPlayer } from '../lib/synth/player';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// Play/seek controls for the synth engine. Drives a <midi-visualizer> (passed by ref) so the
// piano roll follows playback exactly as it did with <midi-player>.
function SynthTransport({ buffer, visualizerRef, onStart, onPause, onEnd, onLoaded }) {
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!buffer) return undefined;
    const player = new SynthPlayer({
      onTime: (t) => setTime(t),
      onActiveNote: (note) => {
        const v = visualizerRef.current;
        if (!v) return;
        if (note) v.redraw(note); else v.clearActiveNotes();
      },
      onStateChange: setPlaying,
      onEnd: () => onEnd?.(),
    });
    try {
      const song = player.load(buffer);
      setDuration(song.duration);
      setTime(0);
      onLoaded?.(song);
    } catch (e) {
      console.error('Could not parse MIDI for the synth player', e);
    }
    playerRef.current = player;
    return () => { player.dispose(); playerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer]);

  const toggle = () => {
    const p = playerRef.current;
    if (!p) return;
    if (p.playing) { p.pause(); onPause?.(); } else { p.play(); onStart?.(); }
  };

  return (
    <div className="flex items-center gap-2 w-full" style={{ height: 'var(--player-height, 40px)' }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        className="flex-shrink-0 flex items-center justify-center rounded text-white bg-[#f05477] hover:bg-[#d94466] active:bg-[#c03355]"
        style={{ width: 'var(--button-width, 40px)', height: 'var(--button-height, 40px)' }}
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
      </button>
      <input
        type="range"
        min={0}
        max={duration || 1}
        step={0.01}
        value={Math.min(time, duration || 1)}
        onChange={(e) => playerRef.current?.seek(parseFloat(e.target.value))}
        aria-label="Seek"
        className="flex-1 min-w-0"
        style={{ accentColor: '#f05477' }}
      />
      <span className="font-mono text-xs text-muted-foreground flex-shrink-0 tabular-nums">
        {fmt(time)} / {fmt(duration)}
      </span>
    </div>
  );
}

export default SynthTransport;
